import React from 'react';
import { Container, Row, Col, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import ace from 'ace-builds/src-noconflict/ace';
import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/theme-tomorrow';
import 'ace-builds/src-noconflict/worker-javascript';
import AceEditor from 'react-ace';
import { shouldWrapText } from '../util/scriptUtils';
import { isValidUrl } from '../util/urlUtils';
import JobSampleManager from './JobSampleManager';
import URLRunSelector from './URLRunSelector';
import HTMLViewer from './HTMLViewer';
import HARViewer from './HARViewer';
import ScreenshotViewer from './ScreenshotViewer';

// Dynamically set the worker path
ace.config.setModuleUrl(
  'ace/mode/javascript_worker',
  new URL('ace-builds/src-noconflict/worker-javascript.js', import.meta.url).toString()
);

class JobLauncher extends React.Component {
    constructor(props, context) {
        super(props, context);
        this.state = {
            startUrlValid: true
        };
        this.onUrlChange = this.onUrlChange.bind(this);
        this.onJobDefinitionSaveAndRunDryJob = this.onJobDefinitionSaveAndRunDryJob.bind(this);
        this.onJobDefinitionSaveAndRunJob = this.onJobDefinitionSaveAndRunJob.bind(this);
        this.isValidRunUrlEntered = this.isValidRunUrlEntered.bind(this);
        this.isJobOutputAvailable = this.isJobOutputAvailable.bind(this);
        this.refreshAceEditor = this.refreshAceEditor.bind(this);
    }

    refreshAceEditor() {
        this.aceOutput.editor.resize();
        this.aceOutput.editor.renderer.updateFull();
    }
    
    onUrlChange(url, siteId, startUrlValid) {
        this.setState({ 
            startUrlValid: startUrlValid
        });
        this.props.stateManagementService.updateJobDefinition(
            { 
                ...this.props.currentJobDefinition, 
                last_test_url: url,
                last_test_site_id: parseInt(siteId)
            }
        ).catch(error => {
            console.error('Failed updating job definition:', error);
        });
    }

    isValidRunUrlEntered() {
        return this.props.currentJobDefinition?.last_test_url != null && isValidUrl(this.props.currentJobDefinition?.last_test_url);
    }

    onJobDefinitionSaveAndRunDryJob() {
        let startURLValid = this.isValidRunUrlEntered()
        this.setState({ startUrlValid: startURLValid });
        if (startURLValid) {
            this.props.saveAndRunDryJob();
        }
    }

    onJobDefinitionSaveAndRunJob() {
        let startURLValid = this.isValidRunUrlEntered()
        this.setState({ startUrlValid: startURLValid });
        if (startURLValid) {
            this.props.saveAndRunJob();
        }
    }

    isJobOutputAvailable() {
        if (!this.props.currentJobDefinition || !this.props.currentJobDefinition.extraction_id) {
            this.props.displayFeedback("Please select a job first.", "failure");
            return false;
        }
        if (!this.props.activeJobId) {
            this.props.displayFeedback("Please start the job first", "failure");
            return false;
        }
        if (this.props.crawling) {
            this.props.displayFeedback("Please wait for the job to complete", "failure");
            return false;
        }
        return true;
    }

    render() {
        return (
            <>
                <Row>
                    <Col md={1} style={{ "height": 10 }}>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <Container>
                            <Row>
                                <Col md={12}>
                                    <URLRunSelector 
                                        jobDefinition={this.props.currentJobDefinition}
                                        onUrlChange={this.onUrlChange}
                                        runUrl={this.props.currentJobDefinition?.last_test_url}
                                        runSiteId={this.props.currentJobDefinition?.last_test_site_id?.toString()}
                                        startUrlValid={this.state.startUrlValid}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col md={1} style={{ "height": 10 }}>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={12}>
                                    <JobSampleManager 
                                        ref={instance => { this.sampleManager = instance; }} 
                                        extractionItem={this.props.currentExtraction}
                                        stateManagementService={this.props.stateManagementService}
                                        partialCrawlConfigurations={this.props.partialCrawlConfigurations}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col md={1} style={{ "height": 10 }}>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={8}>
                                    Test Input
                                </Col>
                            </Row>
                            <Row>
                                <Col md={12}>
                                    <AceEditor
                                        mode="javascript"
                                        theme="tomorrow"
                                        name="debugger"
                                        ref={input => (this.aceDebugger = input)}
                                        width="auto"
                                        height="250px"
                                        style={{ "marginTop": "10px" }}
                                        value={this.props.currentJobDefinition?.last_test_input}
                                        setOptions={{ 
                                            useWorker: true
                                        }}
                                        wrapEnabled={shouldWrapText(this.props.currentJobDefinition?.last_test_input)}
                                        editorProps={{
                                            $blockScrolling: true
                                        }}
                                        onChange={(newValue) => this.props.stateManagementService.updateJobDefinition(
                                            { 
                                                ...this.props.currentJobDefinition, 
                                                last_test_input: newValue
                                            }
                                        ).catch(error => {
                                            console.error('Failed updating job definition:', error);
                                        })}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col md={1} style={{ "height": 10 }}>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={8}>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id="button-tooltip">This will show you what jobs would be spawned or data written, but those actions are not actually taken.</Tooltip>}
                                    >
                                        <Button
                                            variant="success"
                                            className="mb-2 text-end"
                                            onClick={this.onJobDefinitionSaveAndRunDryJob}
                                        >
                                            Save and Run Job in Dry Mode
                                        </Button>
                                    </OverlayTrigger>
                                </Col>
                                <Col md={4}>
                                    <div className="text-end">
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={<Tooltip id="button-tooltip">This will act like a full crawl, but with this job as the initial starting point and the input above supplied.</Tooltip>}
                                        >
                                            <Button
                                                variant="light"
                                                className="mb-2"
                                                onClick={this.onJobDefinitionSaveAndRunJob}
                                            >
                                                Save and Run Job
                                            </Button>
                                        </OverlayTrigger>
                                    </div>
                                </Col>
                            </Row>
                        </Container>
                    </Col>
                    <Col md={6}>
                        <Container>
                            <Row>
                                <Col md={12}>
                                    Crawler Response:
                                </Col>
                            </Row>
                            <Row>
                                <Col md={12}>
                                    <AceEditor
                                        mode="javascript"
                                        theme="tomorrow"
                                        name="job_output"
                                        ref={output => (this.aceOutput = output)}
                                        readOnly={true}
                                        width="auto"
                                        height="500px"
                                        style={{ "marginTop": "10px" }}
                                        value={this.props.currentJobDefinition?.last_test_output}
                                        setOptions={{ 
                                            useWorker: true
                                        }}
                                        wrapEnabled={shouldWrapText(this.props.currentJobDefinition?.last_test_output)}
                                        editorProps={{
                                            $blockScrolling: true
                                        }}
                                    />
                                </Col>
                            </Row>
                            <Row style={{ "marginTop": "10px" }}>
                                <Col className="d-flex justify-content-start" style={{ gap: '10px' }}>
                                <ScreenshotViewer 
                                    ref={instance => { this.screenshotViewer = instance; }} 
                                    row_id={null} activeJobId={this.props.activeJobId} 
                                    currentExtraction={this.props.currentExtraction}
                                    isJobOutputAvailable={this.isJobOutputAvailable}  
                                    batchID={this.props.batchId}
                                />
                                <HTMLViewer 
                                    ref={instance => { this.htmlViewer = instance; }} 
                                    row_id={null} activeJobId={this.props.activeJobId} 
                                    currentExtraction={this.props.currentExtraction}
                                    isJobOutputAvailable={this.isJobOutputAvailable} 
                                    batchID={this.props.batchId}
                                />
                                {/* <HARViewer  
                                    ref={instance => { this.harViewer = instance; }} 
                                    row_id={null} activeJobId={this.props.activeJobId} 
                                    jobDefinition={this.props.currentJobDefinition} 
                                    isJobOutputAvailable={this.isJobOutputAvailable}  
                                /> */}
                                </Col>
                            </Row>
                        </Container>
                    </Col>
                </Row>
            </>
        );
    }
}

export default JobLauncher;